import { AppId, FCX } from "@datassential/platform-ui-lib";
import './SectionMenu.scss';
import FoodSection from "@pages/Food/components/Section/Section";
import FoodWidget from "@pages/Food/components/Widget/Widget";
import { IFoodProfileMenu } from "@models/FoodProfileMenu";
import Versatility from "@pages/Food/components/SectionMenu/Versatility";
import PairedFlavors from "@pages/Food/components/SectionMenu/PairedFlavors";
import cn from "classnames";
import { PermissionLevelEnum } from "@models/PermissionSection";
import PermissionOverlay from "@pages/Food/components/PermissionOverlay/PermissionOverlay";
import { useDispatch } from "react-redux";
import { toast } from "react-toastify";
import { requestTrialByFeature } from "@actions/page";
import ExternalProductLink from "@pages/Food/components/ExternalProductLink/ExternalProductLink";
import FoodLocalPopularity from "@pages/Food/components/SectionMenu/LocalPopularity";
import { useEffect, useMemo, useState } from "react";

const FoodSectionMenu: FCX<{
    data: IFoodProfileMenu;
}> = ({
    data,
}) => {
    const dispatch = useDispatch();
    const [hasLocalData, setHasLocalData] = useState(true);
    const requestTrial: (f: string, hideNotification?: boolean) => any = (featureName, hideNotification) => {
        if (!hideNotification) {
            toast.info('Request has been sent');
        }
        return dispatch(requestTrialByFeature(featureName));
    };

    const {
        name,
        permissionLevel,
        data: {
            menuVersatility,
            foodVersatility,
            pairedFlavors,
            hasLocalPopularity,
            hasConsumerPreferences,
        },
    } = data;

    useEffect(() => {
        setHasLocalData(hasLocalPopularity);
    }, [hasLocalPopularity]);

    const hasAccess = permissionLevel !== PermissionLevelEnum.None;

    const affinityRender = useMemo(
        () => (
            <FoodWidget
                testId="blockFoodMenuVersatility"
                headerContent={(
                    <div data-testid="textFoodMenuVersatilityTitle">
                        VERSATILITY
                    </div>
                )}
                className={cn(
                    hasLocalData ? "FoodSectionMenu__widget-1-1" : "FoodSectionMenu__widget-1-2",
                    hasConsumerPreferences && hasLocalData && "FoodSectionMenu__row-1",
                    !hasConsumerPreferences && !hasLocalData && "FoodSectionMenu__row-2",
                    !hasConsumerPreferences && hasLocalData && "FoodSectionMenu__row-2",
                    hasConsumerPreferences && !hasLocalData && "FoodSectionMenu__row-1",
                )}
            >
                <div className="FoodSectionMenu__widget-1-1-content">
                    <Versatility foodValue={foodVersatility} menuValue={menuVersatility}/>
                </div>
            </FoodWidget>
        ),
        [foodVersatility, menuVersatility, hasConsumerPreferences, hasLocalData]
    );

    const pairedFlavorsRender = useMemo(
        () => (
            <FoodWidget
                testId="blockFoodMenuPairedWith"
                headerContent={(
                    <>
                        <div data-testid="textFoodMenuPairedWithTitle">
                            PAIRED WITH
                        </div>
                        <ExternalProductLink
                            appId={AppId.MenuTrends}
                            path={`${process.env.REACT_APP_MT_BYO_LINK}${data.name}`}
                            testId="buttonFoodMenuPairedWithExternalLink"
                        />
                    </>
                )}
                className={cn(
                    "FoodSectionMenu__widget-1-2",
                    hasConsumerPreferences && hasLocalData && "FoodSectionMenu__row-1",
                    !hasConsumerPreferences && !hasLocalData && "FoodSectionMenu__row-2",
                    !hasConsumerPreferences && hasLocalData && "FoodSectionMenu__row-2",
                    hasConsumerPreferences && !hasLocalData && "FoodSectionMenu__row-2",
                )}
            >
                <div className="FoodSectionMenu__widget-1-2-content">
                    <PairedFlavors data={pairedFlavors}/>
                </div>
            </FoodWidget>
        ),
        [pairedFlavors, data.name, hasConsumerPreferences, hasLocalData]
    );

    const localPopularityRender = useMemo(
        () => {
            if (!hasLocalPopularity) return null;

            return (
                <FoodLocalPopularity
                    word={name}
                    permissionLevel={permissionLevel}
                    setHasLocalData={setHasLocalData}
                />
            );
        },
        [name, permissionLevel, hasLocalPopularity]
    );

    const content = useMemo(
        () => {
            if (!hasConsumerPreferences) {
                if (!hasLocalData) {
                    return (
                        <div className="FoodSection__row">
                            {affinityRender}
                            {pairedFlavorsRender}
                        </div>
                    );
                } else {
                    return (
                        <div className="FoodSection__row">
                            {affinityRender}
                            {pairedFlavorsRender}
                            {localPopularityRender}
                        </div>
                    );
                }
            } else {
                if (!hasLocalData) {
                    return (<>
                        <div className="FoodSection__row">
                            {affinityRender}
                        </div>
                        <div className="FoodSection__row">
                            {pairedFlavorsRender}
                        </div>
                    </>);
                } else {
                    return (<>
                        <div className="FoodSection__row">
                            {affinityRender}
                            {pairedFlavorsRender}
                        </div>
                        <div className="FoodSection__row">
                            {localPopularityRender}
                        </div>
                    </>);
                }
            }
        },
        [affinityRender, pairedFlavorsRender, localPopularityRender, hasConsumerPreferences, hasLocalData]
    );

    return (
        <FoodSection
            testId="blockFoodMenu"
            headerContent={(
                <div data-testid="textFoodMenuTitle">
                    MENU
                </div>
            )}
            className={cn(
                "FoodSectionMenu",
                "appears-top del-2",
                !hasConsumerPreferences && 'is-single-row',
                !hasAccess && "is-blocked"
            )}
        >
            {content}
            {!hasAccess && (
                <PermissionOverlay
                    onContactUsClick={() => requestTrial('mtd')}
                    testId="blockFoodMenuPermissionOverlay"
                >
                    <div data-testid="textFoodMenuPermissionOverlayContentLine-0">
                        Upgrade your subscription to learn
                    </div>
                    <div data-testid="textFoodMenuPermissionOverlayContentLine-1">
                        how menus feature <b>{name}</b>
                    </div>
                </PermissionOverlay>
            )}
        </FoodSection>
    );
};

export default FoodSectionMenu;